import { useTrackActions } from "@/utils/hooks/useTracking";
import { TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import { Box, Button, CircularProgress, Skeleton, styled } from "@mui/material";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";
import { CartTotal } from "./CartTotal";
import { OrderSummaryProduct } from "./OrderSummaryProduct";

const Container = styled("div")({
  boxSizing: "border-box",
  display: "flex",
  borderRadius: "8px",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
  backgroundColor: "#f6f8fa",
  padding: "24px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: "16px",
    backgroundColor: "white",
    padding: "16px",
    maxWidth: "100%",
  },
});

const CartContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

const SectionTitle = styled("div")(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: theme.vars.palette.text.primary,
  marginBottom: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const PlaceOrderButton = styled(Button)({
  width: "100%",
  padding: "14px 24px",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  marginTop: "16px",
});

interface OrderSummaryProps {
  showOrderSummaryTitle?: boolean;
}

const OrderSummary = ({ showOrderSummaryTitle = true }: OrderSummaryProps) => {
  const { session, sessionLoading, productMetadata, triggerPaymentSubmission } =
    useCheckoutSimplifiedUI();
  const [trackAction] = useTrackActions();

  if (sessionLoading) {
    return (
      <Container>
        <Skeleton variant="rectangular" height={25} width="60%" />
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={120} />
      </Container>
    );
  }

  if (!session?.line_items?.length) {
    return null;
  }

  // Extract totals from session
  const getTotalByType = (type: string): number => {
    const total = session.totals.find((t) => t.type === type);
    return total ? total.amount : 0; // Convert from minor units to dollars
  };

  const subtotal = getTotalByType("subtotal");
  const shipping = getTotalByType("fulfillment");
  const estimatedTax = getTotalByType("tax");
  const total = getTotalByType("total");

  // Get delivery estimate from selected fulfillment option
  const selectedOption = session.fulfillment_options.find(
    (opt) => opt.id === session.fulfillment_option_id
  );
  const deliveryEstimate =
    selectedOption?.type === "shipping" && selectedOption.subtitle
      ? selectedOption.subtitle
      : "";

  // Map line items to product display format with real metadata
  const products = session.line_items.map((item) => {
    const metadata = productMetadata[item.item.id];
    return {
      stacklineSku: item.item.id,
      retailerId: metadata?.retailerId,
      retailerSku: metadata?.retailerSku,
      retailerParentSku: metadata?.retailerParentSku,
      title: metadata?.title || `Product ${item.item.id}`,
      brandId: metadata?.brandId,
      brandName: metadata?.brandName,
      categoryId: metadata?.categoryId,
      categoryName: metadata?.categoryName,
      subCategoryId: metadata?.subCategoryId,
      subCategoryName: metadata?.subCategoryName,
      quantity: item.item.quantity,
      retailPrice: metadata?.retailPrice || item.base_amount,
      pixels: metadata?.pixels,
      image: metadata?.image || "",
    };
  });

  const handlePlaceOrderClick = () => {
    // Don't track CHECKOUT_ORDER_SUBMIT - only track success/failure
    // This avoids firing "Order Submitted" pixel events prematurely
    triggerPaymentSubmission?.();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Container>
        {showOrderSummaryTitle && (
          <SectionTitle>
            <span>Order summary</span>
          </SectionTitle>
        )}

        <CartContainer>
          {products.map((item) => (
            <OrderSummaryProduct key={item.stacklineSku} product={item} />
          ))}
        </CartContainer>

        <Box
          sx={{
            width: "100%",
            marginTop: "8px",
          }}
        >
          <CartTotal
            subtotal={subtotal}
            shipping={shipping}
            estimatedTax={estimatedTax}
            total={total}
            itemCount={products.length}
            deliveryEstimate={deliveryEstimate}
            hasShippingAddress={!!session.fulfillment_address}
            hasShippingMethod={!!session.fulfillment_option_id}
          />
        </Box>
      </Container>

      <PlaceOrderButton
        variant="contained"
        color="primary"
        disabled={
          !triggerPaymentSubmission || session?.status === "in_progress"
        }
        onClick={handlePlaceOrderClick}
      >
        {session?.status === "in_progress" ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Processing...
          </>
        ) : !triggerPaymentSubmission ? (
          "Place Order"
        ) : (
          "Place Order"
        )}
      </PlaceOrderButton>
    </Box>
  );
};

export default OrderSummary;
